import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage, languages } from "@/lib/language";
import { Globe } from "lucide-react";
import logoImage from "@/assets/images/icon.png";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function Header() {
  const { language, setLanguage, t } = useLanguage();

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navigateToHome = (sectionId?: string) => {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    // Check if current path has language prefix
    const hasLanguagePrefix =
      firstSegment === "he" || firstSegment === "en" || firstSegment === "fr";

    const homePath = hasLanguagePrefix ? `/${firstSegment}` : `/${language}`;

    // If we're already on the home page, just scroll to section
    const pathWithoutLang = hasLanguagePrefix
      ? "/" + pathSegments.slice(1).join("/")
      : currentPath;

    if (pathWithoutLang === "/" || pathWithoutLang === "") {
      if (sectionId) {
        scrollToSection(sectionId);
      }
    } else {
      // Navigate to home page with section anchor
      const targetPath = sectionId ? `${homePath}#${sectionId}` : homePath;
      window.history.pushState({}, "", targetPath);
      window.location.reload(); // Force reload to trigger route change
    }
  };

  return (
    <header className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <button
            className="flex items-center space-x-2 md:space-x-3"
            onClick={() => navigateToHome()}
            aria-label="Go to home page"
          >
            <img
              src={logoImage}
              alt="Samantha Azoulay Real Estate"
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="text-s sm:text-sm md:text-base lg:text-xl font-bold text-foreground truncate">
                {t("brand")}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground block">
                {t("tagline")}
              </p>
            </div>
          </button>

          <nav className="hidden md:flex items-center space-x-6">
            <Button
              variant="ghost"
              className="text-foreground hover:text-primary-foreground hover:bg-primary"
              onClick={() => navigateToHome("about")}
            >
              {t("about")}
            </Button>
            <Button
              variant="ghost"
              className="text-foreground hover:text-primary-foreground hover:bg-primary"
              onClick={() => navigateToHome("agent")}
            >
              {t("agent")}
            </Button>
            <Button
              variant="ghost"
              className="text-foreground hover:text-primary-foreground hover:bg-primary"
              onClick={() => navigateToHome("contact")}
            >
              {t("contact")}
            </Button>
          </nav>

          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
